import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const PROVIDER_TO_PATH = Object.freeze({ LINE: "line", X: "twitter" });

const getReconfirmProviderPath = (providerName) => {
  if (!providerName || typeof providerName !== "string") return null;
  const normalized = providerName.toUpperCase();
  return PROVIDER_TO_PATH[normalized] ?? null;
};

const WithdrawalPage = ({ csrfToken, withdrawalAuthType, withdrawalProvider }) => {
  const navigate = useNavigate();
  const isOAuth = withdrawalAuthType === "oauth" && Boolean(withdrawalProvider);
  const reconfirmPath = isOAuth ? getReconfirmProviderPath(withdrawalProvider) : null;
  const canReconfirmViaOAuth = Boolean(reconfirmPath);
  const [password, setPassword] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleOpenModal = (e) => {
    e.preventDefault();
    setError("");
    if (!password.trim()) {
      setError("パスワードを入力してください。");
      return;
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => setModalOpen(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");

    const body = new URLSearchParams({
      "user[current_password]": password,
      authenticity_token: csrfToken,
    });

    try {
      const res = await fetch("/withdrawal", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          Accept: "text/html",
        },
        body: body.toString(),
        credentials: "include",
        redirect: "follow",
      });

      if (res.redirected) {
        // サーバー側でリダイレクト理由をクエリパラメータに付与している場合は、
        // ここで読み取ってユーザーに分かりやすいメッセージを表示する
        try {
          const redirectUrl = new URL(res.url, window.location.origin);
          const reason =
            redirectUrl.searchParams.get("reason") ||
            redirectUrl.searchParams.get("redirect_reason");

          if (reason === "unauthorized") {
            // 認証エラー（セッション切れなど）の場合
            toast.error("セッションの有効期限が切れたため、再度ログインしてください。");
          } else if (reason === "validation_error") {
            // サーバー側バリデーションエラーなどで元画面に戻された場合
            toast.error("入力内容に誤りがあります。内容をご確認のうえ、再度お試しください。");
          }
        } catch (urlError) {
          console.warn("[WithdrawalPage] リダイレクトURLのパースに失敗しました", urlError);
        }
        window.location.href = res.url;
        return;
      }

      const text = await res.text();
      if (!res.ok) {
        let message = "退会処理に失敗しました。パスワードをご確認ください。";

        // Try to extract a more specific error message from the server response
        const contentType = res.headers.get("Content-Type") || "";
        if (contentType.includes("application/json")) {
          try {
            const data = JSON.parse(text);
            if (typeof data === "object" && data !== null) {
              const serverMessage =
                data.error || data.message || data.alert || data.notice;
              if (typeof serverMessage === "string" && serverMessage.trim()) {
                message = serverMessage.trim();
              }
            }
          } catch (parseError) {
            console.error(
              "[WithdrawalPage] サーバー応答のJSONパースに失敗しました",
              parseError,
              { responseText: text?.slice(0, 500) }
            );
          }
        }

        setError(message);
        setModalOpen(false);
        toast.error(message);
        return;
      }
    } catch (err) {
      console.error("[WithdrawalPage] 退会処理でエラーが発生しました", err);
      setError("退会処理に失敗しました。しばらく経ってからお試しください。");
      setModalOpen(false);
      toast.error("退会に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 550, mx: "auto", py: 4, px: 2 }}>
      <Button
        onClick={() => navigate("/settings")}
        size="small"
        sx={{
          mb: 0.5,
          color: "#000",
          fontWeight: "bold",
          textTransform: "none",
          "&:hover": { opacity: 0.7 },
        }}
      >
        ← 戻る
      </Button>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
          p: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", textAlign: "center", mb: 1 }}>
          退会
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 3 }}>
          退会するとアカウントに紐づくスキルシート・職務経歴・募集設定など、すべてのデータが削除され、復元できません。
        </Typography>

        {isOAuth ? (
          <Box>
            {canReconfirmViaOAuth ? (
              <>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                  本人確認のため、{withdrawalProvider}で再認証してください。
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  sx={{ fontWeight: "bold" }}
                  href={`/withdrawal/reconfirm/${reconfirmPath}`}
                >
                  {withdrawalProvider}で再認証して退会する
                </Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                退会の本人確認は、現在ご利用のアカウントでは対応していません。お問い合わせください。
              </Typography>
            )}
          </Box>
        ) : (
          <Box component="form" onSubmit={handleOpenModal}>
            <Typography
              component="label"
              htmlFor="withdrawal-password"
              variant="body2"
              sx={{
                display: "block",
                fontWeight: 500,
                color: "text.primary",
                mb: 1,
              }}
            >
              パスワード確認（セキュリティのため再入力してください）
            </Typography>
            <TextField
              id="withdrawal-password"
              fullWidth
              type="password"
              placeholder="現在のパスワードを入力"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!error}
              helperText={error}
              autoComplete="current-password"
              variant="outlined"
              sx={{ mb: 3 }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="error"
              sx={{ fontWeight: "bold" }}
            >
              退会する
            </Button>
          </Box>
        )}
      </Box>

      <Dialog open={modalOpen} onClose={handleCloseModal}>
        <DialogTitle sx={{ fontWeight: "bold" }}>退会の確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当に退会しますか？
            <br />
            アカウントに紐づくすべてのデータが削除され、復元できません。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseModal} variant="outlined" disabled={submitting}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? "処理中..." : "退会する"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

WithdrawalPage.propTypes = {
  csrfToken: PropTypes.string,
  withdrawalAuthType: PropTypes.oneOf(["password", "oauth"]),
  withdrawalProvider: PropTypes.string,
};

WithdrawalPage.defaultProps = {
  csrfToken: "",
  withdrawalAuthType: "password",
  withdrawalProvider: null,
};

export default WithdrawalPage;
